import React, { useState, useEffect } from 'react';
import {
  Users,
  User,
  Phone,
  Mail,
  Search,
  Filter,
  RefreshCw,
  Eye,
  UserCheck,
  Crown,
  Star,
  MapPin,
  Calendar,
  Loader,
  TrendingUp,
  PieChart,
  BarChart3,
  Grid,
  Package,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import salesTeamService from '../services/salesTeamService';
import MiniPieChart from './MiniPieChart';
import ProductSalesDetailModal from './ProductSalesDetailModal';
import CustomerBreakdownModal from './CustomerBreakdownModal';
import { createProfileImageUrl } from '../utils/imageProxy';

// ─── Custom Date Picker ───────────────────────────────────────────────────────
const CustomDatePicker = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => value ? new Date(value + 'T00:00:00') : new Date());
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  React.useEffect(() => {
    if (value) setViewDate(new Date(value + 'T00:00:00'));
  }, [value]);

  const formatDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const selDay   = value ? parseInt(value.split('-')[2]) : null;
  const selMonth = value ? parseInt(value.split('-')[1]) - 1 : null;
  const selYear  = value ? parseInt(value.split('-')[0]) : null;

  const today = new Date();

  const thMonths = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];
  const dayHeaders = ['อา','จ','อ','พ','พฤ','ศ','ส'];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleSelectDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(dateStr);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 min-w-[130px] text-left disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span>{formatDisplay(value) || <span className="text-gray-400">เลือกวันที่</span>}</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-64" style={{ top: '100%', left: 0 }}>
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg text-gray-600 font-bold text-lg"
            >‹</button>
            <span className="font-semibold text-gray-900 text-sm">
              {thMonths[month]} {year + 543}
            </span>
            <button
              type="button"
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg text-gray-600 font-bold text-lg"
            >›</button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {dayHeaders.map(d => (
              <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} />;
              const isSelected = day === selDay && month === selMonth && year === selYear;
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              return (
                <button
                  key={`d-${idx}`}
                  type="button"
                  onClick={() => handleSelectDay(day)}
                  className={`
                    text-sm h-8 rounded-lg text-center transition-colors
                    ${isSelected
                      ? 'bg-blue-600 text-white font-bold'
                      : isToday
                      ? 'bg-blue-50 text-blue-700 font-semibold border border-blue-200'
                      : 'hover:bg-gray-100 text-gray-700'}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const SalesTeamComponent = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [expandedTeam, setExpandedTeam] = useState(new Set());
  const [imageErrors, setImageErrors] = useState({});
  
  // Sales data states
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  
  // MAQ data states
  const [maqData, setMaqData] = useState(null);
  const [maqLoading, setMaqLoading] = useState(false);
  const [maqError, setMaqError] = useState(null);
  
  // RAM data states
  const [ramData, setRamData] = useState(null);
  const [ramLoading, setRamLoading] = useState(false);
  const [ramError, setRamError] = useState(null);

  // PsiCorp data states
  const [psicorpData, setPsicorpData] = useState(null);
  const [psicorpLoading, setPsicorpLoading] = useState(false);
  const [psicorpError, setPsicorpError] = useState(null);
  
  // Date range states
  const [dateRange, setDateRange] = useState(() => {
    const currentMonth = salesTeamService.getCurrentMonthDateRange();
    return {
      startDate: currentMonth.start_date,
      endDate: currentMonth.end_date
    };
  });

  // Category analytics state for each member
  const [memberCategoryData, setMemberCategoryData] = useState({});
  const [expandedMembers, setExpandedMembers] = useState(new Set());
  const [chartViewMembers, setChartViewMembers] = useState(new Set()); // Track which members show chart vs grid

  // Product sales detail modal state
  const [productSalesModal, setProductSalesModal] = useState({
    isOpen: false,
    salesmanCode: '',
    salesmanName: ''
  });

  // Customer breakdown modal state
  const [customerBreakdownModal, setCustomerBreakdownModal] = useState({
    isOpen: false,
    customerType: '',
    customers: []
  });

  // Export loading state
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadTeamsData();
    loadSalesData();
    loadMaqData();
    loadRamData();
    loadPsicorpData();
  }, []);


  const loadTeamsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await salesTeamService.getTeamsWithMembers();
      
      if (response.success) {
        setTeams(response.data);
      } else {
        setError(response.error || 'ไม่สามารถดึงข้อมูลทีมขายได้');
      }
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      console.error('Error loading teams data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSalesData = async () => {
    try {
      setSalesLoading(true);
      setSalesError(null);
      
      const response = await salesTeamService.getTeamSalesSummary(
        new Date(dateRange.startDate),
        new Date(dateRange.endDate)
      );
      
      if (response.success) {
        console.log('Sales data API response:', response);
        console.log('Sales data structure:', JSON.stringify(response.data, null, 2));
        setSalesData(response.data);

        // Sync team member display to match historical membership for the selected date range
        if (response.data?.teams?.length > 0) {
          const historicalTeams = response.data.teams.map(t => ({
            team_id: t.team_id,
            team_name: t.team_name,
            director_id: t.director_id,
            team_saleman_code: t.team_saleman_code,
            members: (t.members_performance || []).map(m => ({
              member_id: m.member_id,
              sale_name: m.sale_name || '',
              sale_lastname: m.sale_lastname || '',
              member_saleman_code: m.member_saleman_code,
              sale_picture: m.sale_picture || null,
              sale_boss: m.sale_boss || null,
              sale_target: m.sale_target || 0,
              sale_target_grop: m.sale_target_grop || 0,
              sale_total: m.sale_total || 0
            }))
          }));
          setTeams(historicalTeams);
        }
      } else {
        setSalesError(response.error || 'ไม่สามารถดึงข้อมูลยอดขายได้');
      }
    } catch (err) {
      setSalesError('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      console.error('Error loading sales data:', err);
    } finally {
      setSalesLoading(false);
    }
  };

  const loadMaqData = async () => {
    try {
      setMaqLoading(true);
      setMaqError(null);
      
      const response = await salesTeamService.getMaqTeamSalesSummary(
        new Date(dateRange.startDate),
        new Date(dateRange.endDate)
      );
      
      if (response.success) {
        console.log('MAQ data API response:', response);
        console.log('MAQ data structure:', JSON.stringify(response.data, null, 2));
        setMaqData(response.data);
      } else {
        setMaqError(response.error || 'ไม่สามารถดึงข้อมูล MAQ ได้');
      }
    } catch (err) {
      setMaqError('เกิดข้อผิดพลาดในการเชื่อมต่อ MAQ');
      console.error('Error loading MAQ data:', err);
    } finally {
      setMaqLoading(false);
    }
  };

  const loadRamData = async () => {
    try {
      setRamLoading(true);
      setRamError(null);
      
      const response = await salesTeamService.getRamTeamSalesSummary(
        new Date(dateRange.startDate),
        new Date(dateRange.endDate)
      );
      
      if (response.success) {
        console.log('RAM data API response:', response);
        console.log('RAM data structure:', JSON.stringify(response.data, null, 2));
        setRamData(response.data);
      } else {
        setRamError(response.error || 'ไม่สามารถดึงข้อมูล RAM ได้');
      }
    } catch (err) {
      setRamError('เกิดข้อผิดพลาดในการเชื่อมต่อ RAM');
      console.error('Error loading RAM data:', err);
    } finally {
      setRamLoading(false);
    }
  };

  const loadPsicorpData = async () => {
    try {
      setPsicorpLoading(true);
      setPsicorpError(null);

      const response = await salesTeamService.getPsicorpTeamSalesSummary(
        new Date(dateRange.startDate),
        new Date(dateRange.endDate)
      );

      if (response.success) {
        console.log('PsiCorp data API response:', response);
        setPsicorpData(response.data);
      } else {
        setPsicorpError(response.error || 'ไม่สามารถดึงข้อมูล PsiCorp ได้');
      }
    } catch (err) {
      setPsicorpError('เกิดข้อผิดพลาดในการเชื่อมต่อ PsiCorp');
      console.error('Error loading PsiCorp data:', err);
    } finally {
      setPsicorpLoading(false);
    }
  };

  const handleRefresh = () => {
    // Reset all cached data when refreshing
    setMemberCategoryData({});
    setExpandedMembers(new Set());
    setChartViewMembers(new Set());
    setExpandedTeam(new Set());
    
    loadTeamsData();
    loadSalesData();
    loadMaqData();
    loadRamData();
    loadPsicorpData();
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchSales = () => {
    if (dateRange.startDate && dateRange.endDate) {
      // Reset all cached category data when searching with new date range
      setMemberCategoryData({});
      setExpandedMembers(new Set());
      setChartViewMembers(new Set());
      setExpandedTeam(new Set());
      
      loadSalesData();
      loadMaqData();
      loadRamData();
      loadPsicorpData();
    }
  };

  const handleExportExcel = async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;
    setExportLoading(true);
    try {
      const response = await salesTeamService.exportTeamMonthly(
        new Date(dateRange.startDate),
        new Date(dateRange.endDate)
      );
      if (!response.success || !response.data || response.data.length === 0) {
        alert('ไม่พบข้อมูลสำหรับ Export ในช่วงเวลาที่เลือก');
        return;
      }

      const rows = response.data;

      // Fetch category analytics for all unique salesmen — fully parallel
      const uniqueCodes = [...new Set(rows.map(r => r.salesman_code))];
      const emptyCategory = { SAT: 0, Solar: 0, MTF: 0, MTB: 0, Other: 0 };
      const categoryMap = new Map();
      const categoryResults = await Promise.allSettled(
        uniqueCodes.map(code => salesTeamService.getSalesmanCategoryAnalytics(
          code, new Date(dateRange.startDate), new Date(dateRange.endDate)
        ))
      );
      categoryResults.forEach((res, idx) => {
        categoryMap.set(uniqueCodes[idx],
          res.status === 'fulfilled' && res.value?.success
            ? (res.value.data?.summary || emptyCategory)
            : emptyCategory
        );
      });

      const addCategory = (code) => {
        const c = categoryMap.get(code) || emptyCategory;
        return {
          'SAT': Math.round(c.SAT || 0),
          'Solar': Math.round(c.Solar || 0),
          'MTF (โรงงาน)': Math.round(c.MTF || 0),
          'MTB (นำเข้า)': Math.round(c.MTB || 0),
          'อื่นๆ': Math.round(c.Other || 0)
        };
      };

      // Sheet "Monthly"
      const monthlySheet = rows.map(r => ({
        'เดือน': r.year_month,
        'ทีม': r.team_name,
        'รหัสพนักงาน': r.salesman_code,
        'ชื่อพนักงาน': `${r.sale_name || ''} ${r.sale_lastname || ''}`.trim(),
        'บิลรวม': r.invoices,
        'ยอดขายรวม (ก่อน VAT)': r.invoice_amount_before_vat,
        'Corp บิล': r.corp_invoices || 0,
        'Corp ยอด': r.corp_amount || 0,
        'RAM บิล': r.ram_invoices || 0,
        'RAM ยอด': r.ram_amount || 0,
        'MAQ บิล': r.maq_invoices || 0,
        'MAQ ยอด': r.maq_amount || 0,
        'CN จำนวน': r.cn_count,
        'CN ยอด (ก่อน VAT)': r.cn_amount_before_vat
      }));

      // Sheet "Summary"
      const summaryMap = new Map();
      rows.forEach(r => {
        const key = `${r.team_id}|${r.salesman_code}`;
        if (!summaryMap.has(key)) {
          summaryMap.set(key, {
            'ทีม': r.team_name,
            'รหัสพนักงาน': r.salesman_code,
            'ชื่อพนักงาน': `${r.sale_name || ''} ${r.sale_lastname || ''}`.trim(),
            'บิลรวม': 0,
            'ยอดขายรวม (ก่อน VAT)': 0,
            'Corp บิล': 0, 'Corp ยอด': 0,
            'RAM บิล': 0, 'RAM ยอด': 0,
            'MAQ บิล': 0, 'MAQ ยอด': 0,
            'CN รวม': 0,
            'CN ยอดรวม (ก่อน VAT)': 0,
            ...addCategory(r.salesman_code)
          });
        }
        const s = summaryMap.get(key);
        s['บิลรวม'] += r.invoices;
        s['ยอดขายรวม (ก่อน VAT)'] += r.invoice_amount_before_vat;
        s['Corp บิล'] += (r.corp_invoices || 0);
        s['Corp ยอด'] += (r.corp_amount || 0);
        s['RAM บิล'] += (r.ram_invoices || 0);
        s['RAM ยอด'] += (r.ram_amount || 0);
        s['MAQ บิล'] += (r.maq_invoices || 0);
        s['MAQ ยอด'] += (r.maq_amount || 0);
        s['CN รวม'] += r.cn_count;
        s['CN ยอดรวม (ก่อน VAT)'] += r.cn_amount_before_vat;
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(monthlySheet), 'Monthly');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(Array.from(summaryMap.values())), 'Summary');

      // Sheet แยกทีม — หนึ่ง sheet ต่อทีม
      const teamIds = [...new Set(rows.map(r => r.team_id))].sort();
      teamIds.forEach(teamId => {
        const teamRows = rows.filter(r => r.team_id === teamId);
        const teamName = teamRows[0]?.team_name || teamId;
        const sheetName = `ทีม_${teamId}`.substring(0, 31);

        // Summary ของทีมนี้ แยกตามรายคน แล้ว subtotal รายเดือน
        const teamSummaryMap = new Map();
        teamRows.forEach(r => {
          const key = r.salesman_code;
          if (!teamSummaryMap.has(key)) {
            teamSummaryMap.set(key, {
              'ทีม': teamName,
              'รหัสพนักงาน': r.salesman_code,
              'ชื่อพนักงาน': `${r.sale_name || ''} ${r.sale_lastname || ''}`.trim(),
              'บิลรวม': 0,
              'ยอดขายรวม (ก่อน VAT)': 0,
              'Corp บิล': 0, 'Corp ยอด': 0,
              'RAM บิล': 0, 'RAM ยอด': 0,
              'MAQ บิล': 0, 'MAQ ยอด': 0,
              'CN รวม': 0,
              'CN ยอดรวม (ก่อน VAT)': 0,
              ...addCategory(r.salesman_code)
            });
          }
          const s = teamSummaryMap.get(key);
          s['บิลรวม'] += r.invoices;
          s['ยอดขายรวม (ก่อน VAT)'] += r.invoice_amount_before_vat;
          s['Corp บิล'] += (r.corp_invoices || 0);
          s['Corp ยอด'] += (r.corp_amount || 0);
          s['RAM บิล'] += (r.ram_invoices || 0);
          s['RAM ยอด'] += (r.ram_amount || 0);
          s['MAQ บิล'] += (r.maq_invoices || 0);
          s['MAQ ยอด'] += (r.maq_amount || 0);
          s['CN รวม'] += r.cn_count;
          s['CN ยอดรวม (ก่อน VAT)'] += r.cn_amount_before_vat;
        });

        // รายละเอียดรายเดือนของทีมนี้
        const teamMonthly = teamRows.map(r => ({
          'เดือน': r.year_month,
          'รหัสพนักงาน': r.salesman_code,
          'ชื่อพนักงาน': `${r.sale_name || ''} ${r.sale_lastname || ''}`.trim(),
          'บิลรวม': r.invoices,
          'ยอดขายรวม (ก่อน VAT)': r.invoice_amount_before_vat,
          'Corp บิล': r.corp_invoices || 0,
          'Corp ยอด': r.corp_amount || 0,
          'RAM บิล': r.ram_invoices || 0,
          'RAM ยอด': r.ram_amount || 0,
          'MAQ บิล': r.maq_invoices || 0,
          'MAQ ยอด': r.maq_amount || 0,
          'CN จำนวน': r.cn_count,
          'CN ยอด (ก่อน VAT)': r.cn_amount_before_vat
        }));

        // รวม summary + เส้นแบ่ง + monthly ไว้ใน sheet เดียว
        const summaryRows = Array.from(teamSummaryMap.values());
        const blankRow = {};
        const headerRow = { 'เดือน': '--- รายละเอียดรายเดือน ---' };
        const combinedRows = [
          ...summaryRows,
          blankRow,
          headerRow,
          ...teamMonthly
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(combinedRows, { skipHeader: false }), sheetName);
      });

      const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const startStr = dateRange.startDate.toString().substring(0, 10);
      const endStr = dateRange.endDate.toString().substring(0, 10);
      saveAs(new Blob([wbOut], { type: 'application/octet-stream' }), `team-sales-${startStr}-to-${endStr}.xlsx`);
    } catch (err) {
      console.error('Export error:', err);
      alert('เกิดข้อผิดพลาดในการ Export');
    } finally {
      setExportLoading(false);
    }
  };

  // Filter teams and members based on search term
  const filteredTeams = teams.filter(team => {
    const teamMatch = team.team_name.toLowerCase().includes(searchTerm.toLowerCase());
    const membersMatch = team.members.some(member =>
      (member.sale_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.sale_lastname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.member_saleman_code || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    const teamFilter = selectedTeam === '' || team.team_id === selectedTeam;
    
    return (teamMatch || membersMatch) && teamFilter;
  }).sort((a, b) => {
    // ส่วนกลาง แสดงท้ายสุดเสมอ
    if (a.team_id === '__central__') return 1;
    if (b.team_id === '__central__') return -1;
    return 0;
  });

  // Get team statistics
  const getTeamStats = () => {
    const totalTeams = teams.length;
    const totalMembers = teams.reduce((sum, team) => sum + team.members.length, 0);
    const avgMembersPerTeam = totalTeams > 0 ? Math.round(totalMembers / totalTeams) : 0;
    
    return { totalTeams, totalMembers, avgMembersPerTeam };
  };

  const stats = getTeamStats();

  // Toggle team expansion
  const toggleTeamExpansion = (teamId) => {
    setExpandedTeam(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };

  // Get director info for a team
  const getDirectorInfo = (team) => {
    return team.members.find(member => member.member_id === team.director_id);
  };

  // Convert image path using SSL-safe proxy
  const convertImagePath = (imagePath) => {
    return createProfileImageUrl(imagePath);
  };

  // Handle image load error
  const handleImageError = (memberId) => {
    setImageErrors(prev => ({
      ...prev,
      [memberId]: true
    }));
  };

  // Get team sales performance from sales data
  const getTeamPerformance = (teamId) => {
    if (!salesData || !salesData.teams) return null;
    
    const teamData = salesData.teams.find(team => team.team_id === teamId);
    return teamData || null;
  };

  // Get team MAQ performance from MAQ data
  const getTeamMaqPerformance = (teamId) => {
    if (!maqData || !maqData.teams) return null;
    
    const maqTeamData = maqData.teams.find(team => team.team_id === teamId);
    return maqTeamData || null;
  };

  // Get team RAM performance from RAM data
  const getTeamRamPerformance = (teamId) => {
    if (!ramData || !ramData.teams) return null;

    const ramTeamData = ramData.teams.find(team => team.team_id === teamId);
    return ramTeamData || null;
  };

  // Get team PsiCorp performance from PsiCorp data
  const getTeamPsicorpPerformance = (teamId) => {
    if (!psicorpData || !psicorpData.teams) return null;

    const psicorpTeamData = psicorpData.teams.find(team => team.team_id === teamId);
    return psicorpTeamData || null;
  };

  // Get member sales performance from team data
  const getMemberPerformance = (teamId, memberId) => {
    const defaultPerformance = { bills: 0, amount: 0, creditNotes: 0, creditNoteAmount: 0, creditNoteRatio: 0, customerCount: 0, salesByCustomerType: { ma: 0, normal: 0, new: 0 }, customerBreakdownByType: { ma: [], normal: [], new: [] } };
    const teamPerformance = getTeamPerformance(teamId);
    if (!teamPerformance || !teamPerformance.members_performance) {
      return defaultPerformance;
    }
    
    const memberPerformance = teamPerformance.members_performance.find(
      member => member.member_id === memberId
    );
    
    return memberPerformance ? {
      bills: memberPerformance.total_bills || memberPerformance.bills || 0,
      amount: memberPerformance.amount_before_vat || 0,
      creditNotes: memberPerformance.credit_notes || 0,
      creditNoteAmount: memberPerformance.credit_note_amount || 0,
      creditNoteRatio: memberPerformance.credit_note_ratio || 0,
      customerCount: memberPerformance.customer_count || 0,
      salesByCustomerType: memberPerformance.sales_by_customer_type || { ma: 0, normal: 0, new: 0 },
      customerBreakdownByType: memberPerformance.customer_breakdown_by_type || { ma: [], normal: [], new: [] }
    } : { bills: 0, amount: 0, creditNotes: 0, creditNoteAmount: 0, creditNoteRatio: 0, customerCount: 0, salesByCustomerType: { ma: 0, normal: 0, new: 0 }, customerBreakdownByType: { ma: [], normal: [], new: [] } };
  };

  // Get member MAQ sales performance from MAQ data
  const getMemberMaqPerformance = (teamId, memberId) => {
    const teamMaqPerf = getTeamMaqPerformance(teamId);
    if (!teamMaqPerf || !teamMaqPerf.members_performance) {
      return { bills: 0, amount: 0 };
    }
    
    const memberMaqPerformance = teamMaqPerf.members_performance.find(
      member => member.member_id === memberId
    );
    
    return memberMaqPerformance ? {
      bills: memberMaqPerformance.total_bills || memberMaqPerformance.bills || 0,
      amount: memberMaqPerformance.amount_before_vat || 0
    } : { bills: 0, amount: 0 };
  };

  // Get member RAM sales performance from RAM data
  const getMemberRamPerformance = (teamId, memberId) => {
    const teamRamPerf = getTeamRamPerformance(teamId);
    if (!teamRamPerf || !teamRamPerf.members_performance) {
      return { bills: 0, amount: 0 };
    }

    const memberRamPerformance = teamRamPerf.members_performance.find(
      member => member.member_id === memberId
    );

    return memberRamPerformance ? {
      bills: memberRamPerformance.total_bills || memberRamPerformance.bills || 0,
      amount: memberRamPerformance.amount_before_vat || 0
    } : { bills: 0, amount: 0 };
  };

  // Get member PsiCorp sales performance from PsiCorp data
  const getMemberPsicorpPerformance = (teamId, memberId) => {
    const teamPsicorpPerf = getTeamPsicorpPerformance(teamId);
    if (!teamPsicorpPerf || !teamPsicorpPerf.members_performance) {
      return { bills: 0, amount: 0 };
    }

    const memberPsicorpPerformance = teamPsicorpPerf.members_performance.find(
      member => member.member_id === memberId
    );

    return memberPsicorpPerformance ? {
      bills: memberPsicorpPerformance.total_bills || memberPsicorpPerformance.bills || 0,
      amount: memberPsicorpPerformance.amount_before_vat || 0
    } : { bills: 0, amount: 0 };
  };

  // Sort members by sales amount (director first, then by amount desc)
  const sortMembersBySales = (members, teamId, directorId) => {
    return [...members].sort((a, b) => {
      // Director always first
      if (a.member_id === directorId) return -1;
      if (b.member_id === directorId) return 1;
      
      // Sort others by sales amount (desc)
      const aPerformance = getMemberPerformance(teamId, a.member_id);
      const bPerformance = getMemberPerformance(teamId, b.member_id);
      
      return bPerformance.amount - aPerformance.amount;
    });
  };

  // Get team total sales - calculate from member data (includes regular, MAQ, and RAM)
  const getTeamTotalSales = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    if (!team || !team.members) return { bills: 0, amount: 0, maqBills: 0, maqAmount: 0, ramBills: 0, ramAmount: 0, psicorpBills: 0, psicorpAmount: 0 };
    
    // Regular sales from members
    const totalBills = team.members.reduce((sum, member) => {
      const memberPerf = getMemberPerformance(teamId, member.member_id);
      return sum + memberPerf.bills;
    }, 0);
    
    const totalAmount = team.members.reduce((sum, member) => {
      const memberPerf = getMemberPerformance(teamId, member.member_id);
      return sum + memberPerf.amount;
    }, 0);

    // MAQ sales
    const teamMaqPerf = getTeamMaqPerformance(teamId);
    const maqBills = teamMaqPerf?.total_bills || 0;
    const maqAmount = teamMaqPerf?.total_before_vat || 0;

    // RAM sales
    const teamRamPerf = getTeamRamPerformance(teamId);
    const ramBills = teamRamPerf?.total_bills || 0;
    const ramAmount = teamRamPerf?.total_before_vat || 0;

    // PsiCorp sales
    const teamPsicorpPerf = getTeamPsicorpPerformance(teamId);
    const psicorpBills = teamPsicorpPerf?.total_bills || 0;
    const psicorpAmount = teamPsicorpPerf?.total_before_vat || 0;

    return {
      bills: totalBills,
      amount: totalAmount,
      maqBills: maqBills,
      maqAmount: maqAmount,
      ramBills: ramBills,
      ramAmount: ramAmount,
      psicorpBills: psicorpBills,
      psicorpAmount: psicorpAmount,
      hasMAQ: maqAmount > 0,
      hasRAM: ramAmount > 0,
      hasPsiCorp: psicorpAmount > 0
    };
  };

  // Handle showing category analytics for a salesman
  const handleShowCategoryAnalytics = async (memberId, salesmanCode, salesmanName, teamId) => {
    // Toggle expansion
    const newExpandedMembers = new Set(expandedMembers);
    if (expandedMembers.has(memberId)) {
      newExpandedMembers.delete(memberId);
      setExpandedMembers(newExpandedMembers);
      return;
    }

    newExpandedMembers.add(memberId);
    setExpandedMembers(newExpandedMembers);

    // Check if we already have data for this member
    if (memberCategoryData[memberId]?.data) {
      return;
    }

    // Set loading state
    setMemberCategoryData(prev => ({
      ...prev,
      [memberId]: {
        loading: true,
        data: null,
        error: null,
        salesmanCode,
        salesmanName
      }
    }));

    try {
      // Get the actual member amount from performance data
      const memberPerformance = getMemberPerformance(teamId, memberId);
      const actualMemberAmount = memberPerformance.amount || 0;
      
      const categoryData = await salesTeamService.getSalesmanCategoryAnalytics(
        salesmanCode,
        dateRange.startDate,
        dateRange.endDate,
        actualMemberAmount,
        memberPerformance.customerBreakdownByType
      );

      setMemberCategoryData(prev => ({
        ...prev,
        [memberId]: {
          loading: false,
          data: categoryData.success ? categoryData.data : null,
          error: categoryData.success ? null : categoryData.error,
          salesmanCode,
          salesmanName
        }
      }));
    } catch (error) {
      setMemberCategoryData(prev => ({
        ...prev,
        [memberId]: {
          loading: false,
          data: null,
          error: 'เกิดข้อผิดพลาดในการเรียกข้อมูล',
          salesmanCode,
          salesmanName
        }
      }));
    }
  };

  // Handle show product sales detail modal
  const handleShowProductSalesDetail = (salesmanCode, salesmanName) => {
    setProductSalesModal({
      isOpen: true,
      salesmanCode,
      salesmanName
    });
  };

  // Handle close product sales detail modal
  const handleCloseProductSalesDetail = () => {
    setProductSalesModal({
      isOpen: false,
      salesmanCode: '',
      salesmanName: ''
    });
  };

  // Handle open customer breakdown modal
  const handleShowCustomerBreakdown = (customerType, customerBreakdownByType, memberName) => {
    const customers = customerBreakdownByType?.[customerType] || [];
    setCustomerBreakdownModal({
      isOpen: true,
      customerType,
      customers,
      memberName: memberName || ''
    });
  };

  // Handle close customer breakdown modal
  const handleCloseCustomerBreakdown = () => {
    setCustomerBreakdownModal({
      isOpen: false,
      customerType: '',
      customers: []
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-gray-600">กำลังโหลดข้อมูลทีมขาย...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <Users className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="text-red-800 font-medium">เกิดข้อผิดพลาด</h3>
          </div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">ทีมขาย</h1>
            <p className="text-gray-600">ข้อมูลทีมขายและสมาชิกในแต่ละทีม</p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            รีเฟรช
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8" />
              <div>
                <p className="text-blue-100 text-sm">จำนวนทีม</p>
                <p className="text-2xl font-bold">{stats.totalTeams}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl">
            <div className="flex items-center gap-3">
              <User className="w-8 h-8" />
              <div>
                <p className="text-green-100 text-sm">จำนวนสมาชิกทั้งหมด</p>
                <p className="text-2xl font-bold">{stats.totalMembers}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Per-team member breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            สมาชิกแต่ละทีม
          </h3>
          <div className="flex flex-wrap gap-2">
            {teams.filter(t => t.team_id !== '__central__').map(team => (
              <div key={team.team_id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-700 font-medium">{team.team_name}</span>
                <span className="text-xs bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full">{team.members.length} คน</span>
              </div>
            ))}
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">ช่วงเวลาข้อมูลยอดขาย</h3>
            {salesLoading && <Loader className="w-4 h-4 text-blue-500 animate-spin" />}
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">วันที่เริ่มต้น:</label>
              <CustomDatePicker
                value={dateRange.startDate}
                onChange={(val) => handleDateChange('startDate', val)}
                disabled={salesLoading}
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">วันที่สิ้นสุด:</label>
              <CustomDatePicker
                value={dateRange.endDate}
                onChange={(val) => handleDateChange('endDate', val)}
                disabled={salesLoading}
              />
            </div>
            
            <button
              onClick={handleSearchSales}
              disabled={salesLoading || !dateRange.startDate || !dateRange.endDate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {salesLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              ค้นหา
            </button>

            <button
              onClick={handleExportExcel}
              disabled={exportLoading || !dateRange.startDate || !dateRange.endDate}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {exportLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export Excel
            </button>

            {salesError && (
              <div className="text-red-600 text-sm">
                {salesError}
              </div>
            )}
          </div>
        </div>


        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาทีม หรือสมาชิก..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="relative">
            <Filter className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white min-w-[150px]"
            >
              <option value="">ทุกทีม</option>
              {teams.map(team => (
                <option key={team.team_id} value={team.team_id}>
                  {team.team_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>


      {/* Teams Grid */}
      <div className="space-y-6">
        {filteredTeams.map((team) => {
          const isCentral = team.team_id === '__central__';
          const director = isCentral ? null : getDirectorInfo(team);
          const isExpanded = !expandedTeam.has(team.team_id);
          const teamSales = getTeamTotalSales(team.team_id);

          return (
            <div key={team.team_id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${isCentral ? 'border-2 border-dashed border-gray-400' : 'border border-gray-200'}`}>
              {/* Team Header */}
              <div
                className={`p-6 cursor-pointer transition-colors ${isCentral ? 'hover:bg-gray-100 bg-gray-50' : 'hover:bg-gray-50'}`}
                onClick={() => toggleTeamExpansion(team.team_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <h3 className={`text-lg font-semibold ${isCentral ? 'text-gray-500' : 'text-gray-900'}`}>{team.team_name}</h3>
                      {!isCentral && <p className="text-gray-600 mb-2">รหัสทีม: {team.team_id} | หัวหน้าทีม: {team.team_saleman_code}</p>}
                      {isCentral && <p className="text-gray-500 text-sm mb-2">พนักงานขายที่ไม่ได้อยู่ในทีม ณ ช่วงเวลาที่เลือก</p>}
                      
                      {/* Branch Details - Only show if has MAQ or RAM */}
                      {(teamSales.hasMAQ || teamSales.hasRAM || teamSales.hasPsiCorp) && (
                        <div className="space-y-1 text-xs">
                          <div className="text-gray-500">
                            ปกติ: <span className="font-semibold text-gray-700">
                              {salesTeamService.formatThaiInteger(teamSales.bills)} บิล | {salesTeamService.formatThaiNumber(teamSales.amount)} บาท
                            </span>
                          </div>
                          {teamSales.hasMAQ && (
                            <div className="text-purple-600">
                              แม่สอด: <span className="font-semibold text-purple-700">
                                {salesTeamService.formatThaiInteger(teamSales.maqBills)} บิล | {salesTeamService.formatThaiNumber(teamSales.maqAmount)} บาท
                              </span>
                            </div>
                          )}
                          {teamSales.hasRAM && (
                            <div className="text-orange-600">
                              รามคำแหง: <span className="font-semibold text-orange-700">
                                {salesTeamService.formatThaiInteger(teamSales.ramBills)} บิล | {salesTeamService.formatThaiNumber(teamSales.ramAmount)} บาท
                              </span>
                            </div>
                          )}
                          {teamSales.hasPsiCorp && (
                            <div className="text-teal-600">
                              PsiCorp: <span className="font-semibold text-teal-700">
                                {salesTeamService.formatThaiInteger(teamSales.psicorpBills)} บิล | {salesTeamService.formatThaiNumber(teamSales.psicorpAmount)} บาท
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Team Sales Summary */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xl font-bold text-gray-900">{team.members.length}</p>
                        <p className="text-xs text-gray-600">สมาชิก</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-blue-600">
                          {salesTeamService.formatThaiInteger(teamSales.bills + teamSales.maqBills + teamSales.ramBills + teamSales.psicorpBills)}
                        </p>
                        <p className="text-xs text-gray-600">ยอดรวมทั้งสิ้น (บิล)</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-green-600">
                          {salesTeamService.formatThaiNumber(teamSales.amount + teamSales.maqAmount + teamSales.ramAmount + teamSales.psicorpAmount)}
                        </p>
                        <p className="text-xs text-gray-600">ยอดรวมทั้งสิ้น (บาท)</p>
                      </div>
                    </div>
                    
                    <Eye className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* Director Info */}
                {director && !isCentral && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-3">
                      {/* Director Profile Picture */}
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-gradient-to-r from-yellow-400 to-yellow-500 border-2 border-yellow-300 shadow-sm">
                        {convertImagePath(director.sale_picture) && !imageErrors[director.member_id] ? (
                          <img 
                            src={convertImagePath(director.sale_picture)} 
                            alt={`${director.sale_name} ${director.sale_lastname}`}
                            className="w-full h-full object-cover"
                            style={{ 
                              imageRendering: 'crisp-edges',
                              imageRendering: '-webkit-optimize-contrast'
                            }}
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onError={() => handleImageError(director.member_id)}
                          />
                        ) : (
                          <Crown className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-yellow-800">
                          หัวหน้าทีม: {director.sale_name} {director.sale_lastname}
                        </p>
                        <p className="text-sm text-yellow-600">รหัส: {director.member_saleman_code}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Team Members */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  <div className="p-4">
                    <h4 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      สมาชิกในทีม ({team.members.length} คน)
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {sortMembersBySales(team.members, team.team_id, team.director_id)
                        .map((member) => {
                          const memberPerformance = getMemberPerformance(team.team_id, member.member_id);
                          
                          return (
                        <div 
                          key={member.member_id} 
                          className={`rounded-lg border transition-colors flex flex-col ${
                            member.member_id === team.director_id 
                              ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                              : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                          }`}
                          style={{
                            minHeight: expandedMembers.has(member.member_id) ? 'auto' : '180px'
                          }}
                        >
                          {(() => {
                            const memberMaqPerf = getMemberMaqPerformance(team.team_id, member.member_id);
                            const memberRamPerf = getMemberRamPerformance(team.team_id, member.member_id);
                            const memberPsicorpPerf = getMemberPsicorpPerformance(team.team_id, member.member_id);
                            const hasMaqSales = memberMaqPerf.amount > 0;
                            const hasRamSales = memberRamPerf.amount > 0;
                            const hasPsicorpSales = memberPsicorpPerf.amount > 0;
                            
                            return (
                              <>
                                {/* Existing content */}
                          {/* Profile Section */}
                          <div className="p-2 flex items-center gap-2 flex-1">
                            {/* Profile Picture */}
                            <div className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-gradient-to-r from-gray-400 to-gray-500 border-2 border-white shadow-sm">
                              {convertImagePath(member.sale_picture) && !imageErrors[member.member_id] ? (
                                <img 
                                  src={convertImagePath(member.sale_picture)} 
                                  alt={`${member.sale_name} ${member.sale_lastname}`}
                                  className="w-full h-full object-cover"
                                  style={{ 
                                    imageRendering: 'crisp-edges',
                                    imageRendering: '-webkit-optimize-contrast'
                                  }}
                                  crossOrigin="anonymous"
                                  referrerPolicy="no-referrer"
                                  onError={() => handleImageError(member.member_id)}
                                />
                              ) : (
                                <User className="w-5 h-5 text-white" />
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-gray-900 truncate text-sm">
                                  {member.sale_name} {member.sale_lastname}
                                </h5>
                                {member.member_id === team.director_id && (
                                  <Crown className="w-4 h-4 text-green-600 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-xs text-gray-600">
                                รหัส: {member.member_saleman_code}
                              </p>
                            </div>
                          </div>
                          
                          {/* Sales Performance */}
                          <div className="border-t border-gray-200 p-2" style={{ minHeight: (() => {
                            let baseHeight = 126; // Base height for basic info
                            if (memberPerformance.creditNotes > 0) baseHeight += 36; // Add for CN section
                            const hasSalesByCustomerType = (memberPerformance.salesByCustomerType.ma + memberPerformance.salesByCustomerType.normal + memberPerformance.salesByCustomerType.new) > 0;
                            if (hasSalesByCustomerType) baseHeight += 36; // Add for customer type section
                            return `${baseHeight}px`;
                          })() }}>
                            <div className="grid grid-cols-3 gap-1.5 mb-1.5">
                              <div className="text-center p-1.5 bg-white rounded border shadow-sm flex flex-col justify-center">
                                <p className="text-xs text-gray-600 mb-0.5 font-medium">บิล</p>
                                <p className="font-bold text-blue-600 text-sm leading-tight">
                                  {salesTeamService.formatThaiInteger(memberPerformance.bills)}
                                </p>
                              </div>
                              <div className="text-center p-1.5 bg-white rounded border shadow-sm flex flex-col justify-center">
                                <p className="text-xs text-gray-600 mb-0.5 font-medium">ลูกค้า</p>
                                <p className="font-bold text-purple-600 text-sm leading-tight">
                                  {salesTeamService.formatThaiInteger(memberPerformance.customerCount)}
                                </p>
                              </div>
                              <div className="text-center p-1.5 bg-white rounded border shadow-sm flex flex-col justify-center">
                                <p className="text-xs text-gray-600 mb-0.5 font-medium">บาท</p>
                                <p className="font-bold text-green-600 text-xs leading-tight break-all">
                                  {salesTeamService.formatThaiNumber(memberPerformance.amount)}
                                </p>
                              </div>
                            </div>
                            
                            {/* Credit Notes (CN) - Show only if has value */}
                            {memberPerformance.creditNotes > 0 && (
                              <div className="mb-1.5">
                                <div className="text-center p-1.5 bg-red-50 rounded border border-red-200 shadow-sm flex flex-col justify-center">
                                  <p className="text-xs text-red-600 mb-1 font-medium">ยอด CN</p>
                                  <p className="font-bold text-red-600 text-xs leading-tight break-all">
                                    {salesTeamService.formatThaiNumber(memberPerformance.creditNoteAmount)}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Sales by Customer Type - Show only if has significant values */}
                            {(memberPerformance.salesByCustomerType.ma + memberPerformance.salesByCustomerType.normal + memberPerformance.salesByCustomerType.new) > 0 && (
                              <div className="mb-1.5">
                                <div className="grid grid-cols-3 gap-1">
                                  <div
                                    className="text-center p-1.5 bg-purple-50 rounded border border-purple-200 shadow-sm cursor-pointer hover:bg-purple-100 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); handleShowCustomerBreakdown('ma', memberPerformance.customerBreakdownByType, `${member.sale_name} ${member.sale_lastname}`); }}
                                  >
                                    <p className="text-xs text-purple-600 font-medium">รายใหญ่</p>
                                    <p className="font-bold text-purple-600 text-xs leading-tight break-all">
                                      {salesTeamService.formatThaiNumber(memberPerformance.salesByCustomerType.ma)}
                                    </p>
                                  </div>
                                  <div
                                    className="text-center p-1.5 bg-blue-50 rounded border border-blue-200 shadow-sm cursor-pointer hover:bg-blue-100 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); handleShowCustomerBreakdown('normal', memberPerformance.customerBreakdownByType, `${member.sale_name} ${member.sale_lastname}`); }}
                                  >
                                    <p className="text-xs text-blue-600 font-medium">ปกติ</p>
                                    <p className="font-bold text-blue-600 text-xs leading-tight break-all">
                                      {salesTeamService.formatThaiNumber(memberPerformance.salesByCustomerType.normal)}
                                    </p>
                                  </div>
                                  <div
                                    className="text-center p-1.5 bg-green-50 rounded border border-green-200 shadow-sm cursor-pointer hover:bg-green-100 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); handleShowCustomerBreakdown('new', memberPerformance.customerBreakdownByType, `${member.sale_name} ${member.sale_lastname}`); }}
                                  >
                                    <p className="text-xs text-green-600 font-medium">ใหม่</p>
                                    <p className="font-bold text-green-600 text-xs leading-tight break-all">
                                      {salesTeamService.formatThaiNumber(memberPerformance.salesByCustomerType.new)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => handleShowCategoryAnalytics(member.member_id, member.member_saleman_code, `${member.sale_name} ${member.sale_lastname}`, team.team_id)}
                                className="px-2 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                              >
                                <PieChart className="w-3 h-3" />
                                {expandedMembers.has(member.member_id) ? 'ซ่อนแยกประเภท' : 'แยกประเภท'}
                              </button>
                              
                              <button
                                onClick={() => handleShowProductSalesDetail(member.member_saleman_code, `${member.sale_name} ${member.sale_lastname}`)}
                                className="px-2 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-1"
                              >
                                <Package className="w-3 h-3" />
                                รายการสินค้า
                              </button>
                            </div>
                          </div>

                          {/* Category Analytics Expanded View */}
                          {expandedMembers.has(member.member_id) && (
                            <div className="border-t border-gray-200 p-3">
                              {memberCategoryData[member.member_id]?.loading ? (
                                <div className="flex items-center justify-center py-4">
                                  <div className="flex items-center gap-2">
                                    <Loader className="w-4 h-4 text-blue-500 animate-spin" />
                                    <p className="text-xs text-gray-600">กำลังโหลด...</p>
                                  </div>
                                </div>
                              ) : memberCategoryData[member.member_id]?.error ? (
                                <div className="space-y-3">
                                  <h5 className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                                    <PieChart className="w-3 h-3" />
                                    ยอดขายแยกประเภท (ก่อน VAT 7%)
                                  </h5>
                                  
                                  {/* Show all categories as 0 on error */}
                                  <div className="grid grid-cols-2 gap-2">
                                    {['SAT', 'Solar', 'MTF', 'MTB', 'Other'].map((category) => (
                                      <div key={category} className="bg-white rounded border p-2">
                                        <p className="text-xs font-medium text-gray-700">{category}</p>
                                        <p className="text-sm font-bold text-blue-600">0.00</p>
                                      </div>
                                    ))}
                                  </div>

                                  <div className="bg-blue-50 rounded border border-blue-200 p-2">
                                    <div className="flex justify-between items-center">
                                      <p className="text-xs font-semibold text-blue-800">รวมทั้งหมด</p>
                                      <p className="text-sm font-bold text-blue-800">0.00 บาท</p>
                                    </div>
                                  </div>

                                  <p className="text-xs text-red-500 text-center">
                                    ไม่สามารถโหลดข้อมูลได้
                                  </p>
                                </div>
                              ) : memberCategoryData[member.member_id]?.data ? (
                                <div className="space-y-3">
                                  <h5 className="text-xs font-semibold text-gray-700 flex items-center gap-1 mb-3">
                                    <PieChart className="w-3 h-3" />
                                    ยอดขายแยกประเภท (ก่อน VAT 7%)
                                  </h5>
                                  
                                  {/* Combined Chart and Data View */}
                                  <div className="flex gap-3">
                                    {/* Left: Chart */}
                                    <div className="flex-shrink-0">
                                      <MiniPieChart 
                                        data={memberCategoryData[member.member_id].data.summary}
                                        width={120}
                                        height={100}
                                      />
                                    </div>
                                    
                                    {/* Right: Data Grid */}
                                    <div className="flex-1 grid grid-cols-2 gap-2">
                                      {['SAT', 'Solar', 'MTF', 'MTB', 'Other'].map((category) => {
                                        const getColorClass = (cat) => {
                                          switch(cat) {
                                            case 'SAT': return 'text-blue-600';
                                            case 'Solar': return 'text-yellow-600';
                                            case 'MTF': return 'text-green-600';
                                            case 'MTB': return 'text-teal-600';
                                            case 'Other': return 'text-gray-600';
                                            default: return 'text-gray-600';
                                          }
                                        };

                                        // Calculate percentage for this category
                                        const categoryAmount = memberCategoryData[member.member_id].data.summary[category] || 0;
                                        const totalAmount = memberCategoryData[member.member_id].data.total || 0;
                                        const percentage = totalAmount > 0 ? ((categoryAmount / totalAmount) * 100).toFixed(0) : 0;
                                        
                                        return (
                                          <div key={category} className="bg-white rounded border p-2">
                                            <p className="text-xs font-medium text-gray-700">
                                              {category} {percentage > 0 ? `(${percentage}%)` : ''}
                                            </p>
                                            <p className={`text-sm font-bold ${getColorClass(category)}`}>
                                              {salesTeamService.formatThaiNumber(categoryAmount)}
                                            </p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Total */}
                                  <div className="bg-blue-50 rounded border border-blue-200 p-2">
                                    <div className="flex justify-between items-center">
                                      <p className="text-xs font-semibold text-blue-800">รวมทั้งหมด</p>
                                      <p className="text-sm font-bold text-blue-800">
                                        {salesTeamService.formatThaiNumber(memberCategoryData[member.member_id].data.total)} บาท
                                      </p>
                                    </div>
                                  </div>

                                  <p className="text-xs text-gray-500 text-center">
                                    ประมวลผล {memberCategoryData[member.member_id].data.invoiceCount}/{memberCategoryData[member.member_id].data.totalInvoices} ใบ
                                    {/* Debug info - can remove in production */}
                                    {(() => {
                                      const calculatedTotal = Object.values(memberCategoryData[member.member_id].data.summary)
                                        .reduce((sum, amount) => sum + amount, 0);
                                      const actualTotal = memberCategoryData[member.member_id].data.total;
                                      const difference = Math.abs(calculatedTotal - actualTotal);
                                      
                                      return difference > 0.01 ? (
                                        <span className="block text-xs text-orange-600">
                                          ปรับยอด: {salesTeamService.formatThaiNumber(difference)}
                                        </span>
                                      ) : null;
                                    })()}
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <h5 className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                                    <PieChart className="w-3 h-3" />
                                    ยอดขายแยกประเภท (ก่อน VAT 7%)
                                  </h5>
                                  
                                  {/* Show all categories as 0 when no data */}
                                  <div className="grid grid-cols-2 gap-2">
                                    {['SAT', 'Solar', 'MTF', 'MTB', 'Other'].map((category) => {
                                      const getColorClass = (cat) => {
                                        switch(cat) {
                                          case 'SAT': return 'text-blue-600';
                                          case 'Solar': return 'text-yellow-600';
                                          case 'MTF': return 'text-green-600';
                                          case 'MTB': return 'text-teal-600';
                                          case 'Other': return 'text-gray-600';
                                          default: return 'text-gray-600';
                                        }
                                      };
                                      return (
                                        <div key={category} className="bg-white rounded border p-2">
                                          <p className="text-xs font-medium text-gray-700">{category}</p>
                                          <p className={`text-sm font-bold ${getColorClass(category)}`}>0.00</p>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  <div className="bg-blue-50 rounded border border-blue-200 p-2">
                                    <div className="flex justify-between items-center">
                                      <p className="text-xs font-semibold text-blue-800">รวมทั้งหมด</p>
                                      <p className="text-sm font-bold text-blue-800">0.00 บาท</p>
                                    </div>
                                  </div>

                                  <p className="text-xs text-gray-500 text-center">
                                    ไม่มีข้อมูลยอดขาย
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* MAQ Sales Footer */}
                          {hasMaqSales && (
                            <div className="border-t border-purple-200 bg-purple-50 px-3 py-2">
                              <div className="text-center">
                                <p className="text-xs text-purple-700 font-medium">
                                  แม่สอด {salesTeamService.formatThaiNumber(memberMaqPerf.amount)} บาท
                                </p>
                              </div>
                            </div>
                          )}

                          {/* RAM Sales Footer */}
                          {hasRamSales && (
                            <div className="border-t border-orange-200 bg-orange-50 px-3 py-2">
                              <div className="text-center">
                                <p className="text-xs text-orange-700 font-medium">
                                  รามคำแหง {salesTeamService.formatThaiNumber(memberRamPerf.amount)} บาท
                                </p>
                              </div>
                            </div>
                          )}

                          {/* PsiCorp Sales Footer */}
                          {hasPsicorpSales && (
                            <div className="border-t border-teal-200 bg-teal-50 px-3 py-2">
                              <div className="text-center">
                                <p className="text-xs text-teal-700 font-medium">
                                  PsiCorp {salesTeamService.formatThaiNumber(memberPsicorpPerf.amount)} บาท
                                </p>
                              </div>
                            </div>
                          )}
                              </>
                            );
                          })()}
                        </div>
                      )})}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* No Results */}
      {filteredTeams.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">ไม่พบข้อมูลทีมขาย</h3>
          <p className="text-gray-600">ลองเปลี่ยนคำค้นหาหรือตัวกรองใหม่</p>
        </div>
      )}

      {/* Product Sales Detail Modal */}
      <ProductSalesDetailModal
        isOpen={productSalesModal.isOpen}
        onClose={handleCloseProductSalesDetail}
        salesmanCode={productSalesModal.salesmanCode}
        salesmanName={productSalesModal.salesmanName}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
      />

      {/* Customer Breakdown Modal */}
      <CustomerBreakdownModal
        isOpen={customerBreakdownModal.isOpen}
        onClose={handleCloseCustomerBreakdown}
        customerType={customerBreakdownModal.customerType}
        customers={customerBreakdownModal.customers}
        memberName={customerBreakdownModal.memberName}
      />

    </div>
  );
};

export default SalesTeamComponent;